'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils/cn';
import { Icon, ALL_ICON_TYPES } from './icons/icon';

// Use the unified icon type from the Icon component
export type IconName = (typeof ALL_ICON_TYPES)[number];

const iconButtonVariants = cva(
  [
    // Base styles - circular button with glass effect and smooth transitions
    'inline-flex items-center justify-center rounded-full transition-all duration-300 ease-out cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-95',
  ],
  {
    variants: {
      variant: {
        // Default - transparent with glass hover
        default: ['bg-transparent text-foreground', 'hover:glass-surface hover:shadow-sm'],
        // Selected - glass surface background
        selected: [
          'glass-surface text-foreground shadow-sm',
          'hover:glass-surface-hover hover:shadow-md',
        ],
        // Ghost - transparent with subtle glass on hover
        ghost: ['bg-transparent text-foreground', 'hover:glass-surface hover:shadow-sm'],
        // Primary - glass button effect
        primary: ['glass-button text-white', 'hover:shadow-lg hover:scale-[1.05]'],
        // Submit - primary glass button
        submit: [
          'glass-button text-white [&_svg]:text-white',
          'hover:shadow-lg hover:scale-[1.05]',
        ],
        // Danger - glass with danger colors
        danger: [
          'bg-transparent text-foreground',
          'hover:bg-danger-100 hover:text-white hover:[&>svg]:text-white hover:shadow-md active:bg-danger-600 active:text-white active:[&>svg]:text-white',
        ],
        // Danger ghost - transparent red variant with glass
        'danger-ghost': ['bg-transparent text-danger', 'hover:glass-surface hover:text-danger-600'],
      },
      size: {
        xs: 'h-5 w-5', // 20px
        sm: 'h-8 w-8', // 32px
        md: 'h-10 w-10', // 40px - default
        lg: 'h-12 w-12', // 48px
        xl: 'h-14 w-14', // 56px
      },
      iconSize: {
        xs: '[&_svg]:h-3 [&_svg]:w-3', // 12px icon
        sm: '[&_svg]:h-3 [&_svg]:w-3', // 12px icon
        md: '[&_svg]:h-4 [&_svg]:w-4', // 16px icon - default
        lg: '[&_svg]:h-5 [&_svg]:w-5', // 20px icon
        xl: '[&_svg]:h-6 [&_svg]:w-6', // 24px icon
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      iconSize: 'md',
    },
  }
);

export interface IconButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButtonVariants> {
  iconName: IconName;
  'aria-label': string; // Make aria-label required for accessibility
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, iconSize, iconName, ...props }, ref) => {
    // Map iconSize to Icon component size
    const getIconSize = (iconSize?: string | null) => {
      switch (iconSize) {
        case 'xs':
        case 'sm':
          return 'sm';
        case 'md':
          return 'default';
        case 'lg':
          return 'md';
        case 'xl':
          return 'lg';
        default:
          return 'default';
      }
    };

    // Special styling for icons that need centering adjustments - relative to size
    let iconSpecificStyles = '';
    if (iconName === 'Send') {
      switch (size) {
        case 'xs':
          iconSpecificStyles = '[&_svg]:translate-x-[-0.5px] [&_svg]:translate-y-[0.5px]';
          break;
        case 'sm':
          iconSpecificStyles = '[&_svg]:translate-x-[-1px] [&_svg]:translate-y-[1px]';
          break;
        case 'md':
          iconSpecificStyles = '[&_svg]:translate-x-[-1.5px] [&_svg]:translate-y-[1.5px]';
          break;
        case 'lg':
          iconSpecificStyles = '[&_svg]:translate-x-[-2px] [&_svg]:translate-y-[2px]';
          break;
        case 'xl':
          iconSpecificStyles = '[&_svg]:translate-x-[-2.5px] [&_svg]:translate-y-[2.5px]';
          break;
        default:
          iconSpecificStyles = '[&_svg]:translate-x-[-1.5px] [&_svg]:translate-y-[1.5px]'; // default to md
      }
    }

    return (
      <button
        ref={ref}
        className={cn(
          iconButtonVariants({ variant, size, iconSize }),
          iconSpecificStyles,
          className
        )}
        {...props}
      >
        <Icon iconType={iconName} size={getIconSize(iconSize)} />
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };

// Export commonly used icon names for convenience
export const commonIcons = {
  // Navigation
  chevronLeft: 'ChevronLeft',
  chevronRight: 'ChevronRight',
  chevronUp: 'ChevronUp',
  chevronDown: 'ChevronDown',
  arrowLeft: 'ArrowLeft',
  arrowRight: 'ArrowRight',

  // Actions
  plus: 'Plus',
  minus: 'Minus',
  x: 'X',
  check: 'Check',
  edit: 'Edit',
  trash: 'Trash2',
  copy: 'Copy',
  download: 'Download',
  upload: 'Upload',

  // Interface
  menu: 'Menu',
  moreHorizontal: 'MoreHorizontal',
  moreVertical: 'MoreVertical',
  settings: 'Settings',
  search: 'Search',
  filter: 'Filter',

  // Status
  info: 'Info',
  alertTriangle: 'AlertTriangle',
  alertCircle: 'AlertCircle',
  checkCircle: 'CheckCircle',

  // Media
  play: 'Play',
  pause: 'Pause',
  stop: 'Square',

  // Voice to text
  mic: 'Mic',
  stopCircle: 'StopCircle',
  square: 'Square',
  loader: 'Loader',

  // Communication
  mail: 'Mail',
  phone: 'Phone',
  messageCircle: 'MessageCircle',

  // Custom icons
  screenCapture: 'ScreenCaptureIcon',
} as const satisfies Record<string, IconName>;
